import type { CreateContractFormSchemaType } from "@/pages/shipper-contract-create/model/validation";
import { BaseAccordion } from "@/shared/ui/base-accordion";
import { BaseAccordionSummary } from "@/shared/ui/base-accordion-summary";
import { BaseFormLabel } from "@/shared/ui/base-form-label";
import { BaseNumberField } from "@/shared/ui/base-number-field";
import { BaseSelect } from "@/shared/ui/base-select";
import { BaseTextField } from "@/shared/ui/base-text-field";
import {
  AccordionDetails,
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
} from "@mui/material";
import { FreightMatching } from "@x-mile/api-contract";
import { type Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

// パレット, ケース, 紙帯, フレコン, 荷物そのまま, カゴ台車, コンテナ, ドラム缶, 番重, その他]
const PACKING_STYLES_LIST = Object.keys(
  FreightMatching.schemas.OfferCargoPackingStyle,
) as Array<keyof typeof FreightMatching.schemas.OfferCargoPackingStyle>;

export const CargoForm = ({
  control,
}: {
  control: Control<CreateContractFormSchemaType>;
}) => {
  const { t } = useTranslation();
  return (
    <BaseAccordion>
      <BaseAccordionSummary>荷物情報</BaseAccordionSummary>
      <AccordionDetails>
        <BaseFormLabel label="品名" sx={{ maxWidth: "376px" }}>
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <BaseTextField
                {...field}
                size="small"
                fullWidth
                error={!!fieldState.invalid}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </BaseFormLabel>
        <BaseFormLabel
          label="荷姿"
          sx={{ mt: 3, maxWidth: "376px" }}
          isRequired
        >
          <Controller
            name="packingStyle"
            control={control}
            render={({ field, fieldState }) => {
              return (
                <FormControl fullWidth error={fieldState.invalid} size="small">
                  <BaseSelect {...field} type="string">
                    {PACKING_STYLES_LIST.map((item) => (
                      <MenuItem key={item} value={item}>
                        {t(`packingStyle.${item}`)}
                      </MenuItem>
                    ))}
                  </BaseSelect>
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              );
            }}
          />
        </BaseFormLabel>
        <Box display="flex" gap={3}>
          <BaseFormLabel label="数量" sx={{ mt: 3 }}>
            <Box display="flex" gap={1} sx={{ alignItems: "flex-end" }}>
              <Controller
                name="count"
                control={control}
                render={({ field, fieldState }) => (
                  <BaseNumberField
                    {...field}
                    size="small"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Box>
          </BaseFormLabel>
          <BaseFormLabel label="総重量" sx={{ mt: 3 }}>
            <Box display="flex" gap={1} sx={{ alignItems: "flex-end" }}>
              <Controller
                name="weight"
                control={control}
                render={({ field, fieldState }) => (
                  <BaseNumberField
                    {...field}
                    size="small"
                    unit="t"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Box>
          </BaseFormLabel>
          <BaseFormLabel label="寸法(長さ)" sx={{ mt: 3 }}>
            <Box display="flex" gap={1} sx={{ alignItems: "flex-end" }}>
              <Controller
                name="depth"
                control={control}
                render={({ field }) => (
                  <BaseNumberField
                    {...field}
                    size="small"
                    unit="cm"
                    value={field.value ?? undefined}
                  />
                )}
              />
            </Box>
          </BaseFormLabel>
          <BaseFormLabel label="寸法(幅)" sx={{ mt: 3 }}>
            <Box display="flex" gap={1} sx={{ alignItems: "flex-end" }}>
              <Controller
                name="width"
                control={control}
                render={({ field, fieldState }) => (
                  <BaseNumberField
                    {...field}
                    type="number"
                    size="small"
                    unit="cm"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Box>
          </BaseFormLabel>
          <BaseFormLabel label="寸法(高さ)" sx={{ mt: 3 }}>
            <Box display="flex" gap={1} sx={{ alignItems: "flex-end" }}>
              <Controller
                name="height"
                control={control}
                render={({ field, fieldState }) => (
                  <BaseNumberField
                    {...field}
                    size="small"
                    unit="cm"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Box>
          </BaseFormLabel>
        </Box>
      </AccordionDetails>
    </BaseAccordion>
  );
};
